import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, DollarSign, Gauge, TrendingUp, Target, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import api from '../../utils/api';

const PERIODS = [
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom Range' },
];

// Returns { start, end } as ISO strings (or nulls) for a given preset key.
const rangeFor = (key) => {
  const now = new Date();
  const end = now.toISOString();
  if (key === 'all') return { start: null, end: null };
  if (key === 'last7' || key === 'last30') {
    const days = key === 'last7' ? 7 : 30;
    const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return { start: start.toISOString(), end };
  }
  if (key === 'week') {
    const d = new Date(now);
    const dow = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - dow);
    d.setHours(0, 0, 0, 0);
    return { start: d.toISOString(), end };
  }
  if (key === 'month') {
    const d = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    return { start: d.toISOString(), end };
  }
  return { start: null, end: null };
};

const MetricCard = ({ testId, icon: Icon, label, value, sub, accent }) => (
  <Card data-testid={testId} className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
    <CardContent className="pt-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</span>
        <div className="p-2 rounded-lg" style={{ backgroundColor: accent.bg }}>
          <Icon className="h-4 w-4" style={{ color: accent.fg }} />
        </div>
      </div>
      <div className="text-3xl font-black text-[#1A1A1A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{value}</div>
      {sub && <p className="text-xs text-[#9CA3AF] mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

const WinningTheWeek = () => {
  const [period, setPeriod] = useState('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeRange = useMemo(() => {
    if (period === 'custom') {
      const start = customStart ? new Date(`${customStart}T00:00:00`).toISOString() : null;
      const end = customEnd ? new Date(`${customEnd}T23:59:59`).toISOString() : null;
      return { start, end };
    }
    return rangeFor(period);
  }, [period, customStart, customEnd]);

  const loadStats = async (range) => {
    setLoading(true);
    try {
      const params = {};
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const res = await api.get('/admin/biz-dev-stats', { params });
      setStats(res.data);
    } catch (e) {
      console.error('Failed to load biz-dev stats', e);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load for presets; for custom, wait until at least one date is chosen.
  useEffect(() => {
    if (period === 'custom' && !customStart && !customEnd) return;
    loadStats(activeRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, activeRange.start, activeRange.end]);

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString()}`;
  const fmtNum = (n) => (n === null || n === undefined ? '—' : n);

  return (
    <Card data-testid="winning-the-week-section" className="bg-white border border-[#E5E7EB] shadow-none relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] via-[#7B6FE8] to-[#4ECDC4]"></div>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-black text-[#1A1A1A] uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              <Trophy className="h-5 w-5 text-[#6B5CE7]" /> Winning The Week
            </h2>
            <p className="text-[#6B7280] mt-0.5 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Biz-dev scoreboard — quotes bound, premium closed & pipeline velocity</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                data-testid={`period-btn-${p.key}`}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                  period === p.key
                    ? 'bg-[#6B5CE7] text-white border-[#6B5CE7]'
                    : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#7B6FE8]/40 hover:text-[#6B5CE7]'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {period === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
            <div className="flex flex-col">
              <label className="text-xs font-medium text-[#6B7280] mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>From</label>
              <input
                type="date"
                data-testid="custom-start-date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-md border border-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7]/30"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-[#6B7280] mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>To</label>
              <input
                type="date"
                data-testid="custom-end-date"
                value={customEnd}
                min={customStart || undefined}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 text-sm rounded-md border border-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#6B5CE7]/30"
              />
            </div>
            {!customStart && !customEnd && (
              <span className="text-xs text-[#9CA3AF] pb-2">Pick a start and/or end date to view metrics</span>
            )}
          </div>
        )}

        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
          <MetricCard
            testId="metric-quotes-bound"
            icon={Trophy}
            label="Quotes Bound"
            value={fmtNum(stats?.quotes_bound)}
            accent={{ bg: '#ECFDF5', fg: '#065F46' }}
          />
          <MetricCard
            testId="metric-premium-closed"
            icon={DollarSign}
            label="Premium Closed"
            value={fmtMoney(stats?.premium_closed)}
            accent={{ bg: '#EEF0FF', fg: '#6B5CE7' }}
          />
          <MetricCard
            testId="metric-avg-days"
            icon={Gauge}
            label="Avg Days to Close"
            value={stats?.avg_days_to_close === null || stats?.avg_days_to_close === undefined ? '—' : stats.avg_days_to_close}
            sub="submitted → won"
            accent={{ bg: '#FFFBEB', fg: '#92400E' }}
          />
          <MetricCard
            testId="metric-deals-advanced"
            icon={TrendingUp}
            label="Deals Advanced"
            value={fmtNum(stats?.deals_advanced)}
            sub={stats?.forward_events ? `${stats.forward_events} stage moves` : 'moved a stage'}
            accent={{ bg: '#EEF0FF', fg: '#7B6FE8' }}
          />
          <MetricCard
            testId="metric-win-rate"
            icon={Target}
            label="Win Rate"
            value={stats?.win_rate === null || stats?.win_rate === undefined ? '—' : `${stats.win_rate}%`}
            sub={stats ? `${stats.quotes_bound} won · ${stats.closed_lost} lost` : null}
            accent={{ bg: '#ECFDF5', fg: '#065F46' }}
          />
          <MetricCard
            testId="metric-new-applications"
            icon={FileText}
            label="New Applications"
            value={fmtNum(stats?.new_applications)}
            accent={{ bg: '#EEF0FF', fg: '#6B5CE7' }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default WinningTheWeek;
